import ActionButton from '@/components/ActionButton';
import { getPageTitle, PageSEO } from '@/components/SEO';
import TextInput from '@/components/TextInput';
import siteMetadata from '@/data/siteMetadata';
import { FormEvent, useCallback, useMemo, useState } from 'react';
import { useMutate } from 'restful-react';

interface CareersFormBody {
  name: string;
  email: string;
  phone_number?: string;
  notes?: string;
}

const CareersPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [resume, setResume] = useState<File | undefined>(undefined);
  const [notes, setNotes] = useState('');

  const { mutate, loading, error } = useMutate<CareersFormBody>({
    path: '/api/careers',
    verb: 'POST',
  });

  const canSubmit = useMemo(() => {
    return name.length > 0 && email.length > 0;
  }, [name, email]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) return;

      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('phone_number', phoneNumber);
      formData.append('notes', notes);

      if (resume !== undefined) {
        formData.append('resume', resume);
      }

      mutate(formData);
      setSubmitted(true);
    },
    [canSubmit, email, mutate, name, notes, phoneNumber, resume]
  );

  const submitButtonText = useMemo(() => {
    if (submitted) return 'Thank you!';
    if (loading) return 'Sending...';
    return 'Submit';
  }, [loading, submitted]);

  return (
    <>
      <PageSEO title={getPageTitle('Careers')} description={siteMetadata.description} />

      <div className="my-8 grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-2">
        <div className="col-span-1 p-6">
          <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-100">
            Join our growing team.
          </h1>
          <p className="text-left text-sm font-normal text-gray-700 dark:text-gray-200">
            Fill out the form or email{' '}
            <a href="mailto:hello@exafunction.com">hello@exafunction.com</a>.
          </p>
        </div>
        <div className="col-span-1">
          <div className="rounded-md bg-white p-10 text-left shadow-2xl">
            <h2 className="text-2xl leading-7 text-gray-900 dark:text-gray-100">Join Us</h2>

            <form onSubmit={handleSubmit}>
              <TextInput label="Name" onValueChange={setName} value={name} id="name" required />
              <TextInput
                label="Email"
                onValueChange={setEmail}
                value={email}
                id="email"
                required
                type="email"
              />
              <TextInput
                label="Phone Number"
                onValueChange={setPhoneNumber}
                value={phoneNumber}
                id="phoneNumber"
                type="tel"
              />
              <div className="my-6">
                <label className="block text-sm font-medium leading-5 text-gray-700 dark:text-gray-200">
                  Resume (PDF)
                </label>
                <div className="mt-1 rounded-md">
                  <input
                    className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    id="resume"
                    type="file"
                    onChange={(event) => setResume(event.target.files?.[0])}
                  />
                </div>
              </div>

              <div className="my-6">
                <label className="mb-1 block text-sm text-gray-700">
                  Tell us a bit about yourself
                </label>
                <textarea
                  className="focus:shadow-outline w-full appearance-none rounded border py-2 px-3 leading-tight text-gray-700 shadow focus:outline-none"
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes"
                />
              </div>

              <div className="h-2 w-full" />
              <ActionButton disabled={submitted || !canSubmit} type="submit">
                {submitButtonText}
              </ActionButton>
            </form>
            {error && (
              <div className="w-72 pt-2 text-sm text-red-500 dark:text-red-400 sm:w-96">
                {error.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CareersPage;
